import * as THREE from 'three'
import Experience from '../Experience.js'

// Couche de nuages traversée pendant la descente d'ouverture. Texture
// générée (amas de halos doux), sprites sans brouillard ; chaque nuage
// s'efface à l'approche de la caméra pour une traversée sans « pop ».
export default class Clouds {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.config = this.experience.config.course.flyover

        this.group = new THREE.Group()
        this.group.visible = false
        this.scene.add(this.group)

        this.texture = this.createTexture()
        this.clouds = []
        this.opacity = 0
    }

    // Cumulus : amas de boules aux cœurs denses, base aplatie et ombrée
    // (blanc au sommet, gris bleuté dessous).
    createTexture() {
        const size = 512
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')

        // Graine fixe : même silhouette à chaque chargement
        let seed = 7
        const random = () => {
            seed = (seed * 16807) % 2147483647
            return (seed - 1) / 2147483646
        }

        const base = size * 0.66
        for (let i = 0; i < 42; i++) {
            const x = size * (0.18 + random() * 0.64)
            const spread = 1 - Math.abs(x / size - 0.5) * 1.6
            const radius = size * (0.07 + random() * 0.09) * (0.6 + spread * 0.6)
            const y = base - radius * (0.35 + random() * 0.9) * (0.5 + spread)

            const shade = THREE.MathUtils.clamp((y - size * 0.3) / (base - size * 0.3), 0, 1)
            const r = Math.round(255 - shade * 34)
            const g = Math.round(255 - shade * 26)
            const b = Math.round(255 - shade * 16)

            const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius)
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.95)`)
            gradient.addColorStop(0.6, `rgba(${r}, ${g}, ${b}, 0.8)`)
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`)
            ctx.fillStyle = gradient
            ctx.beginPath()
            ctx.arc(x, y, radius, 0, Math.PI * 2)
            ctx.fill()
        }

        // Base adoucie : le bas du nuage se fond au lieu d'être coupé net
        ctx.globalCompositeOperation = 'destination-out'
        const fade = ctx.createLinearGradient(0, base - size * 0.04, 0, base + size * 0.06)
        fade.addColorStop(0, 'rgba(0, 0, 0, 0)')
        fade.addColorStop(1, 'rgba(0, 0, 0, 1)')
        ctx.fillStyle = fade
        ctx.fillRect(0, base - size * 0.04, size, size)
        ctx.globalCompositeOperation = 'source-over'

        const texture = new THREE.CanvasTexture(canvas)
        texture.colorSpace = THREE.SRGBColorSpace
        return texture
    }

    // Répartit les nuages autour de la colonne de descente
    layout(column) {
        this.clear()

        // Sur mobile, moitié moins de nuages : ces grands sprites
        // transparents se superposent et coûtent cher au GPU
        const count = this.experience.isMobile
            ? (this.config.cloudCountMobile ?? 45)
            : (this.config.cloudCount ?? 90)
        const height = this.config.cloudHeight ?? 3.6
        const center = column.start.clone().lerp(column.end, 0.35)

        let seed = 11
        const random = () => {
            seed = (seed * 16807) % 2147483647
            return (seed - 1) / 2147483646
        }

        for (let i = 0; i < count; i++) {
            const material = new THREE.SpriteMaterial({
                map: this.texture,
                color: new THREE.Color('#fffaf4'),
                transparent: true,
                depthWrite: false,
                fog: false,
                opacity: 0
            })

            const sprite = new THREE.Sprite(material)
            const angle = random() * Math.PI * 2
            const distance = Math.sqrt(random()) * 6.5
            const scale = 1.8 + random() * 2.8

            sprite.position.set(
                center.x + Math.cos(angle) * distance,
                height + (random() - 0.5) * 0.6,
                center.y + Math.sin(angle) * distance
            )
            sprite.scale.set(scale * 1.6, scale, 1)
            sprite.renderOrder = 20
            sprite.userData.baseOpacity = 0.85 + random() * 0.15

            this.group.add(sprite)
            this.clouds.push(sprite)
        }
    }

    show(column) {
        this.layout(column)
        this.group.visible = true
    }

    hide() {
        this.group.visible = false
        this.clear()
    }

    clear() {
        for (const cloud of this.clouds) {
            cloud.material.dispose()
            this.group.remove(cloud)
        }
        this.clouds = []
    }

    // amount : opacité globale (fondu d'apparition / de sortie)
    update(camera, amount = 1) {
        if (!this.group.visible) return

        for (const cloud of this.clouds) {
            const distance = cloud.position.distanceTo(camera.position)
            const near = THREE.MathUtils.smoothstep(distance, 0.5, 2.2)
            cloud.material.opacity = cloud.userData.baseOpacity * near * amount
        }
    }

    destroy() {
        this.clear()
        this.texture.dispose()
        this.scene.remove(this.group)
    }
}
